/**
 * Modal Field Adapter
 * Transforms Product Field Registry into Modal field configurations
 */

import { FieldConfig } from '../../../components/modals/ModalFieldRenderer';
import {
  ManufacturerWithGrade,
  Product,
} from '../../../services/api/domains/products/types';
import { PRODUCT_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { formatDateTime } from '../../../utils/dateConversion';
import { stripHtmlTags } from '../../../utils/renderHtml';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';
import { Manufacturer } from '../../../services/api/domains/manufacturers/types';
import { Facility } from '../../../services/api/domains/facilities/types';
import { Grade } from '../../../services/api/domains/grades/types';
import { TherapeuticArea } from '../../../services/api/domains/therapeutic-areas/types';

/**
 * Defines the layout of a single section in the product modal.
 * `DETAILS_TAB_SECTIONS` is composed of these entries. `toModalFields`/
 * `toSectionFields` iterate the active config and produce one `FieldConfig`
 * per listed key (keys absent from `PRODUCT_FIELDS_REGISTRY` are skipped).
 */
export interface ModalSectionConfig {
  /** Heading displayed above the section in the modal */
  title: string;
  /** Ordered list of `PRODUCT_FIELDS_REGISTRY` key values to render in this section */
  fields: string[];
  /** When true the section renders inside a collapsible wrapper */
  collapsible?: boolean;
  /** Initial collapsed state — only meaningful when `collapsible` is true */
  defaultCollapsed?: boolean;
  /**
   * When true, this is the section ProductModal injects the OB/DMF/NDC/CAS
   * source panels after (issue #622, Sub-task 8.1). An explicit flag on the
   * config entry itself, rather than ProductModal matching on `title` text —
   * the previous approach (a `Set` of title strings ProductModal maintained
   * independently of this file) silently breaks if a title here is ever
   * renamed without updating that Set in lockstep. Exactly one entry across
   * the active section array should set this.
   */
  hostsSourcePanels?: boolean;
  /**
   * When true, ProductModal renders this section's body as the compact
   * checkmark/X status summary (renderStatusSummary) instead of a
   * FieldConfig-driven form — same rationale as `hostsSourcePanels`: an
   * explicit flag rather than matching on `title === 'Status'`, so renaming
   * the heading can't silently break the summary rendering.
   */
  isStatusSummary?: boolean;
}

/**
 * Details-tab layout (Decision 5). `ob_product_name`/`product_cas_name` are
 * dropped from Product Identity (Requirement 2 AC3) — their editable input
 * relocates to the Names tab (Task 11.1). The read-only "Sourced from"
 * preview moved to the modal header (post-review redesign) instead of
 * staying in this section, so "External Sources" hosts only the OB/DMF/CAS/
 * NDC panels (flagged via this entry's own `hostsSourcePanels: true` —
 * issue #622, Sub-task 8.1 — rather than ProductModal matching on section
 * title) and has no FieldConfig-driven fields of its own.
 *
 * `facilities` is temporarily hidden from Associations pending a review of
 * whether/how to re-enable it — see issue #597.
 */
export const DETAILS_TAB_SECTIONS: ModalSectionConfig[] = [
  {
    title: 'Associations',
    fields: ['manufacturers'],
  },
  {
    title: 'External Sources',
    fields: [],
    hostsSourcePanels: true,
  },
  {
    title: 'Status',
    fields: [],
    isStatusSummary: true,
  },
  {
    title: 'Metadata',
    fields: [
      'therapeutic_areas',
      'note',
      'user_id',
      'created_date',
      'modified_date',
    ],
  },
];

/**
 * Options for modal field generation (API-provided data)
 */
export interface ProductFieldOptions {
  manufacturers?: Manufacturer[];
  facilities?: Facility[];
  grades?: Grade[];
  therapeuticAreas?: TherapeuticArea[];
}

/**
 * Convert a basic field definition to modal field config
 */
function createStandardField(field: BaseFieldDefinition): FieldConfig<Product> {
  // Determine field type
  let fieldType: 'text' | 'textarea' | 'number' | 'date' | 'readonly' = 'text';

  if (field.isReadOnly) {
    fieldType = 'readonly';
  } else if (field.renderConfig?.rows && field.renderConfig.rows > 1) {
    fieldType = 'textarea';
  } else if (field.dataType === 'number') {
    fieldType = 'number';
  } else if (field.dataType === 'date') {
    fieldType = 'date';
  }

  const config: FieldConfig<Product> = {
    key: field.key as keyof Product,
    label: field.label,
    type: fieldType,
    getValue: (p: Product) => {
      const value = p[field.key as keyof Product];
      if (
        field.renderConfig?.formatAs === 'html' &&
        typeof value === 'string'
      ) {
        return stripHtmlTags(value);
      }
      // Format boolean values for display
      if (field.dataType === 'boolean') {
        if (value === null || value === undefined) return 'Unknown';
        return value ? 'Yes' : 'No';
      }
      return value || '';
    },
    setValue: field.isReadOnly
      ? undefined
      : (p: Product, val: unknown) => ({
          ...p,
          [field.key]: val || undefined,
        }),
    isAlwaysDisabled: field.isReadOnly,
    isRequired: field.isRequired,
    tooltipText: field.description,
    rows: field.renderConfig?.rows,
  };

  // Add formatter for date fields
  if (field.dataType === 'date' && field.isReadOnly) {
    config.formatter = formatDateTime;
  }

  return config;
}

/**
 * Create async multiselect field for relations without status
 * Uses API-based pagination and search instead of pre-loaded data
 */
function createAsyncMultiselectField(
  field: BaseFieldDefinition
): FieldConfig<Product> {
  const { relationConfig } = field;
  if (!relationConfig) {
    throw new Error(
      `Field ${String(field.key)} is marked as relation but missing relationConfig`
    );
  }

  const { entityName, idField } = relationConfig;

  // Extract async select config from field registry with modal context
  // Modal context uses ID field for value (database storage)
  const contextConfig = getContextAwareConfigFromRegistry(
    PRODUCT_FIELDS_REGISTRY,
    field.key as string,
    'modal'
  );

  if (!contextConfig) {
    throw new Error(
      `Field ${String(field.key)} is missing async select configuration`
    );
  }

  // Convert to standard config for component props
  const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

  return {
    key: field.key as keyof Product,
    label: field.label,
    type: 'async_multiselect',
    getValue: (p: Product) => {
      const items = p[field.key as keyof Product] as unknown as Array<
        Record<string, unknown>
      >;
      if (!Array.isArray(items)) return [];
      return items.map(item => item[idField]?.toString() || '');
    },
    setValue: (p: Product, val: unknown) => {
      const selectedIds = Array.isArray(val) ? val : [];
      const items = selectedIds
        .map(id => {
          // Create minimal objects with just ID and empty name
          // The AsyncMultiSelectField will fetch the full data
          if (entityName === 'grades') {
            return { grade_id: parseInt(id, 10), grade_name: '' };
          } else if (entityName === 'facilities') {
            return { facility_id: parseInt(id, 10), facility_name: '' };
          } else if (entityName === 'therapeutic_areas') {
            return {
              therapeutic_area_id: parseInt(id, 10),
              therapeutic_area_name: '',
            };
          }
          return null;
        })
        .filter(Boolean);

      return { ...p, [field.key]: items };
    },
    apiEndpoint: asyncConfig.apiEndpoint,
    searchField: asyncConfig.searchField,
    valueField: asyncConfig.valueField,
    labelField: asyncConfig.labelField,
    isRequired: field.isRequired,
    tooltipText: field.description,
  };
}

/**
 * Create list builder field for relations with status (manufacturers)
 */
function createListBuilderField(
  field: BaseFieldDefinition,
  apiData: ProductFieldOptions,
  hideProductType: boolean
): FieldConfig<Product> {
  const { relationConfig } = field;
  if (!relationConfig) {
    throw new Error(
      `Field ${String(field.key)} is marked as relation but missing relationConfig`
    );
  }

  const { idField, displayField, statusField } = relationConfig;

  // Get available items from API data
  const availableItems =
    apiData.manufacturers?.map(m => ({
      id: m.manufacturer_id,
      label: m.manufacturer_name,
    })) || [];

  return {
    key: field.key as keyof Product,
    label: field.label,
    type: 'list_builder',
    getValue: (p: Product) => {
      const items = p[field.key as keyof Product] as unknown as Array<
        Record<string, unknown>
      >;
      if (!Array.isArray(items)) return [];
      const perMfrGrades = p.grades || [];
      return items.map(item => {
        // A manufacturer can legitimately supply a product in multiple grades
        // (Products_Manufacturers_Grades is many-to-many per product+manufacturer) —
        // collect every matching entry, not just the first.
        const gradeEntries = perMfrGrades.filter(
          g => g.manufacturer_id === (item[idField] as number)
        );
        return {
          id: item[idField],
          label: item[displayField],
          status: statusField ? item[statusField] : null,
          fields: {
            grade_ids: gradeEntries
              .filter(g => g.grade_id != null)
              .map(g => String(g.grade_id)),
            grade_name: gradeEntries
              .filter(g => g.grade_id != null)
              .map(g => g.grade_name ?? String(g.grade_id)),
            man_product_name:
              item['man_product_name'] != null
                ? String(item['man_product_name'])
                : null,
          },
        };
      });
    },
    setValue: (p: Product, val: unknown) => {
      const items = Array.isArray(val)
        ? val.map(item => {
            const manufacturer = apiData.manufacturers?.find(
              m => m.manufacturer_id === item.id
            );
            const gradeIdsField = item.fields?.grade_ids;
            const gradeIds: number[] = Array.isArray(gradeIdsField)
              ? Array.from(
                  new Set(
                    gradeIdsField
                      .map((gid: string) => Number(gid))
                      .filter((n: number) => Number.isInteger(n))
                  )
                )
              : [];
            return {
              manufacturer_id: item.id,
              manufacturer_name: manufacturer?.manufacturer_name || item.label,
              manufacturing_status: item.status || null,
              grade_ids: gradeIds,
              man_product_name: item.fields?.man_product_name ?? null,
            };
          })
        : [];
      // Keep p.grades in sync with the updated per-manufacturer grade assignments.
      // getValue() reads p.grades to populate the multi-select's initial values —
      // without this, the displayed grades revert to the stale original on each render.
      const updatedGrades: ManufacturerWithGrade[] = items.flatMap(m =>
        m.grade_ids.map(gradeId => {
          const existingEntry = (p.grades || []).find(
            g =>
              g.manufacturer_id === m.manufacturer_id && g.grade_id === gradeId
          );
          return {
            manufacturer_id: m.manufacturer_id,
            manufacturing_status: m.manufacturing_status,
            grade_id: gradeId,
            grade_name: existingEntry?.grade_name ?? null,
          };
        })
      );
      return { ...p, [field.key]: items, grades: updatedGrades };
    },
    availableItems,
    listBuilderConfig: {
      idKey: 'Manufacturer',
      labelKey: 'Manufacturer',
      badgeText: 'ASSOCIATED MANUFACTURERS',
      statusKey: 'Manufacturing Status',
      statusOptions: field.selectionOptions || [],
      statusColumns: [
        // hide_product_type AB flag: omit the "Product Type" column while
        // leaving grade_ids/grade_name data flow in getValue/setValue above
        // untouched, so existing assignments survive an unrelated edit.
        ...(hideProductType
          ? []
          : [
              {
                kind: 'async-multi' as const,
                key: 'grade_ids',
                label: 'Product Type',
                asyncFetch: '/grades',
                asyncSearchField: 'grade_name',
                asyncValueField: 'grade_id',
                asyncLabelField: 'grade_name',
              },
            ]),
        {
          kind: 'text',
          key: 'man_product_name',
          label: 'Manufacturer Product Name',
          maxLength: 450,
        },
      ],
      addButtonText: 'Add Manufacturer',
      addModalTitle: 'Add Manufacturers',
      noItemsText: 'No manufacturers associated with this product',
    },
    isRequired: field.isRequired,
    tooltipText: field.description,
  };
}

/**
 * Create field for preferred_product_name / Product Name.
 *
 * VIEW mode: shows the pre-resolved display_name from the detail endpoint (OQ-29 Option C)
 *            with a source label when the name is resolved from fallback.
 * EDIT mode: text input for preferred_product_name; clearing sends null (not "").
 *            ob_product_name shown read-only as contextual information.
 *            SynonymsRadioField below pre-fills the text input on selection.
 */
function createPreferredProductNameField(): FieldConfig<Product> {
  return {
    key: 'preferred_product_name',
    label: 'Product Name',
    type: 'synonyms_radio',
    getValue: (p: Product) => ({
      preferredValue: p.preferred_product_name || '',
      synonyms: p.synonyms || [],
      displayName: p.display_name || '',
      displayNameSource: p.display_name_source ?? null,
    }),
    setValue: (p: Product, val: unknown) => ({
      ...p,
      preferred_product_name: (val as string) || null,
    }),
    tooltipText:
      'The Product Name is sometimes set as the official CAS Registry name (often a chemical formula). Use Preferred Product Name to set a simpler, common name for internal reference. Select from synonyms below or enter a custom name.',
  };
}

/**
 * Convert Product Field Registry to Modal field configurations.
 * Field order and grouping are driven by the caller-supplied `sections` config.
 * Any number of named `ModalSectionConfig[]` arrays are supported.
 * Defaults to `DETAILS_TAB_SECTIONS` when no config is provided.
 *
 * Fields absent from the active config are omitted (no explicit skip list needed).
 *
 * @param apiData - Data from API calls (manufacturers, facilities, etc.)
 * @param sections - Active section layout config; defaults to `DETAILS_TAB_SECTIONS`
 * @returns Flat array of FieldConfig in section-config order
 */
export function toModalFields(
  apiData: ProductFieldOptions = {},
  sections: ModalSectionConfig[] = DETAILS_TAB_SECTIONS,
  hideProductType = false
): FieldConfig<Product>[] {
  return sections.flatMap(section =>
    toSectionFields(apiData, section, hideProductType)
  );
}

/**
 * Build the field configs for a single modal section.
 * Used by `ProductModal` to render each section independently,
 * allowing collapsible sections to be wrapped in `<CollapsibleSection>`.
 *
 * @param apiData - Data from API calls (manufacturers, facilities, etc.)
 * @param section - The section config to build fields for
 * @returns Fields for that section only, in config order
 */
export function toSectionFields(
  apiData: ProductFieldOptions,
  section: ModalSectionConfig,
  hideProductType = false
): FieldConfig<Product>[] {
  // Build O(1) lookup from registry
  const registryMap = new Map(PRODUCT_FIELDS_REGISTRY.map(f => [f.key, f]));

  const fields: FieldConfig<Product>[] = [];

  for (const key of section.fields) {
    const field = registryMap.get(key);
    // Key not in registry, or the registry doesn't mark it as a modal
    // surface (issue #585 Task 5) — skip silently. product_cas_name is the
    // historical example: fully retired from PRODUCT_FIELDS_REGISTRY
    // (issue #585 Task 9), so `registryMap.get(key)` returns undefined and
    // it's skipped by the `!field` branch.
    if (!field || !field.surfaces?.includes('modal')) continue;

    // Special case: preferred_product_name renders as synonyms_radio picker
    if (field.key === 'preferred_product_name') {
      fields.push(createPreferredProductNameField());
      continue;
    }

    // Special case: replaced_rns is a string[] — render readonly
    if (field.key === 'replaced_rns') {
      fields.push({
        key: 'replaced_rns' as keyof Product,
        label: field.label,
        type: 'readonly',
        getValue: (p: Product) => {
          const rns = p.replaced_rns;
          if (!Array.isArray(rns) || rns.length === 0) return '';
          return rns.join(', ');
        },
      });
      continue;
    }

    // Relational fields
    if (field.isRelation && field.relationConfig) {
      const hasStatus = !!field.relationConfig.statusField;
      if (hasStatus) {
        fields.push(createListBuilderField(field, apiData, hideProductType));
      } else {
        fields.push(createAsyncMultiselectField(field));
      }
      continue;
    }

    // Standard scalar fields
    if (field.dataType !== 'array' && field.dataType !== 'object') {
      fields.push(createStandardField(field));
    }
  }

  return fields;
}

/**
 * Get always disabled field names for products
 */
export function getAlwaysDisabledFields(): string[] {
  return PRODUCT_FIELDS_REGISTRY.filter(f => f.isReadOnly).map(
    f => f.key as string
  );
}
